import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import useFollowingBadge from '../hooks/useFollowingBadge';
import useAuthorityProfile from '../hooks/useAuthorityProfile';
import { Home, Calendar, Tag, Heart, User, Building2, Landmark, Search, Inbox, Menu } from 'lucide-react';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasUpdates: followingHasUpdates } = useFollowingBadge();
  // Iter 205a — light call to detect official authority so we can render
  // an amber dot on the profile icon and a tooltip "Official Authority mode".
  const authority = useAuthorityProfile();

  const isBusiness = user?.role === 'business';

  // Auth-gated destinations.
  const protectedPaths = ['/profile', '/business/dashboard', '/calendar', '/notifications'];

  // Dashboard redesign (2026) — bottom bar shortcuts: Home · Search · Calendar
  // · Inbox · Menu. All point to EXISTING routes/pages.
  const navItems = [
    { path: '/home', icon: Home, label: t('nav.home', 'Home'), testId: 'nav-home' },
    { path: '/explore', icon: Search, label: t('nav.search', 'Search'), testId: 'nav-search' },
    { path: '/calendar', icon: Calendar, label: t('nav.calendar', 'Calendar'), testId: 'nav-calendar' },
    { path: '/notifications', icon: Inbox, label: t('nav.inbox', 'Inbox'), testId: 'nav-inbox' },
    { path: '/profile', icon: Menu, label: t('nav.menu', 'Menu'), testId: 'nav-menu', showDot: followingHasUpdates },
  ];

  const isActive = (path) => {
    if (path === '/explore') return location.pathname === '/explore' || location.pathname === '/discover';
    if (path === '/home') return location.pathname === '/home' || location.pathname === '/';
    return location.pathname === path;
  };

  const handleNav = (path) => {
    if (!user && protectedPaths.includes(path)) {
      navigate('/signin');
    } else {
      navigate(path);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom z-50">
      {/* Iter 205a — Authority mode indicator strip.
          A 2px amber accent line + small label on the profile slot so an
          official can see at a glance that they're in Authority mode no
          matter which page they're on. */}
      {authority.isOfficial && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5 bg-amber-500"
          data-testid="authority-mode-strip"
          title={`Official Authority mode — ${authority.displayName || ''}`}
          aria-hidden="true"
        />
      )}
      <div className="flex justify-around items-center h-14 max-w-7xl mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const isProfile = item.path === '/profile';
          return (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                active ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={item.testId}
            >
              <span className="relative">
                <Icon className={`h-5 w-5 ${active ? 'fill-purple-600' : ''}`} />
                {item.showDot && (
                  <span
                    className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"
                    data-testid="following-update-dot"
                    aria-label="New updates from businesses you follow"
                  />
                )}
                {/* Iter 205a — Authority badge on Profile icon. */}
                {isProfile && authority.isOfficial && (
                  <span
                    className="absolute -top-1 -right-2 inline-flex items-center justify-center h-3.5 w-3.5 rounded-full bg-amber-500 ring-2 ring-white shadow"
                    data-testid="authority-mode-badge"
                    title={`${authority.displayName} · ${authority.town}`}
                  >
                    <Landmark className="h-2 w-2 text-white" />
                  </span>
                )}
              </span>
              <span className="text-[10px] mt-0.5 font-medium leading-tight">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
